import { ReactNode, useContext, useEffect, useRef, useState } from 'react';

import poolSuccessImgPath from 'assets/pngs/carve-confirmation.png';
import { createQueryFilters } from 'context/DataContext';
import { PillFilters } from 'features/common-elements';
import { LoanTable } from 'features/drilldown/LoanDatatable/LoanTable';
import { InProgressOverlay } from 'features/loanTape/InProgressOverlay';
import { useTourContext } from 'tours/TourProvider';
import { TOUR_CONFIGS } from 'tours/config';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  Button,
  DownloadDocumentButton,
  SelectionStatus,
  SelectionStatusLine,
  useDialog,
  DialogActions,
  Dropdown,
  ModalNotStyled,
} from 'ui-kit';
import { downloadExcelFromJson } from 'utils/excelUtils';

import { useApolloClient } from '@apollo/client';

import { GetDrilldown, GetDrilldownVariables } from 'query/__generated__/GetDrilldown';
import { GetPools_user_company_pools as Pool } from 'query/__generated__/GetPools';
import { GET_DRILLDOWN_QUERY } from 'query/drilldown';

import CreatePoolForm, { FormValues } from './CreatePoolForm';
import { GetPoolLoans, GetPoolLoansVariables } from './gql/__generated__/GetPoolLoans';
import { GET_POOL_LOANS_QUERY, usePoolLoans } from './gql/getPoolLoans_gql';
import { useCreatePool } from './hooks/useCreatePool';
import { useLoanLevelData } from './hooks/useLoanLevelData';
import { usePoolOptions } from './hooks/usePoolOptions';
import { usePoolSelection } from './hooks/usePoolSelection';
import { usePortfolioLoans } from './hooks/usePortfolioLoans';
import { useSelection } from './hooks/useSelection';
import { CreDetailsModal } from './Portfolio/CreDetailsModal';
import { PortfolioLayout } from './Portfolio/PortfolioLayout';
import { usePortfolioContext, transformSelectedPoolsData } from './PortfolioContext';
import { AssetClass, SortDirection } from '../../../__generated__/globalTypes';
import { DataContext, DataContextActionType } from '../../../context/DataContext';
import { useFeatureFlag } from '../../../context/FeatureFlagContext';
import { useColumnOrder } from '../../../hooks/useColumnOrder';

/**
 * Reusable dialog component for pool creation success/started states.
 * Extracts common styling patterns used across pool dialogs.
 */
interface PoolSuccessDialogProps {
  dialog: ReturnType<typeof useDialog>;
  title: string;
  message: string;
  actions: ReactNode;
  imageAlt?: string;
}

function PoolSuccessDialog({
  dialog,
  title,
  message,
  actions,
  imageAlt = 'pool success',
}: PoolSuccessDialogProps) {
  return (
    <Dialog dialog={dialog}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <img src={poolSuccessImgPath} className="z-10 h-[232px]" alt={imageAlt} />
        <div className="mb-2 mt-[19px] text-center text-[24px] font-medium leading-[36px] text-white">
          {title}
        </div>
        <div className="mb-[32px] w-[300px] text-[16px] leading-[24px] tracking-[0px] text-white">
          {message}
        </div>
        {actions}
      </div>
    </Dialog>
  );
}

//temporary while not assetClasses are available
export const getLoanTableConfigName = (assetsClass: AssetClass | 'HOME_ARM' | null) => {
  switch (assetsClass) {
    case AssetClass.HOME:
      return 'HOME';
    case AssetClass.AUTO:
      return 'AUTO';
    case AssetClass.UNSECPERSONAL:
      return 'UNSECPERSONAL';
    case 'HOME_ARM':
      return 'HOME_ARM';
    case AssetClass.CRE:
      return AssetClass.CRE;
    default:
      return 'HOME';
  }
};

const TOUR_START_DELAY_MS = 300;
const MAX_RETRY_ATTEMPTS = 20;
const RETRY_DELAY_MS = 150;

const LoanLevelData = () => {
  const { state, dispatch } = useContext(DataContext);
  const apolloClient = useApolloClient();
  const { startTour, tourDataLoading } = useTourContext();
  const hasStartedTours = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTourRef = useRef(startTour);
  const retryAttemptsRef = useRef(0);

  const {
    gqlAssetClass,
    filterProps,
    navigateToPoolManager,
    queryFilters,
    loansSorting,
    setLoansSorting,
    assetClass,
  } = useLoanLevelData();

  // Pool selection state
  const [selectedPool, setSelectedPool] = useState<{
    label: string;
    value: string;
    poolData: Pool;
  } | null>(null);

  // Get pool URL parameter
  const { setPool } = filterProps;

  const { poolOptions } = usePoolOptions({
    assetClass: assetClass,
  });

  // Portfolio context for summary display
  const { setSelectedPoolsData } = usePortfolioContext();

  // Dialogs
  const poolCreatedDialog = useDialog();
  const poolCreationStartedDialog = useDialog();
  const errorDialog = useDialog();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { createPool, lastCreatedPool, pendingPoolJob, createPoolLoading } = useCreatePool();
  const isCreEnabled = useFeatureFlag('creEnabled');

  const navigate = useNavigate();
  let location = useLocation();

  // Use pool loans query when pool is selected, otherwise use company loans query
  const {
    data: poolData,
    loading: poolLoading,
    previousData: poolPreviousData,
    params: poolParams,
    updateParams: poolUpdateParams,
  } = usePoolLoans(selectedPool?.value || null, queryFilters, loansSorting);

  const {
    data: companyData,
    loading: companyLoading,
    previousData: companyPreviousData,
    params: companyParams,
    updateParams: companyUpdateParams,
  } = usePortfolioLoans(gqlAssetClass, queryFilters, loansSorting);

  // Use pool data if pool is selected, otherwise use company data
  const data = selectedPool ? poolData : companyData;
  const loading = selectedPool ? poolLoading : companyLoading;
  const previousData = selectedPool ? poolPreviousData : companyPreviousData;
  const params = selectedPool ? poolParams : companyParams;
  const updateParams = selectedPool ? poolUpdateParams : companyUpdateParams;

  const [creModalLoanId, setCreModalLoanId] = useState<string>('');
  const [isOpenCREModal, setIsOpenCREModal] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const handleCREClick = (loanId: string) => {
    setIsOpenCREModal(true);
    setCreModalLoanId(loanId);
    navigate(`${location.pathname}/${loanId}`);
  };

  const handleCloseCREModal = () => {
    setIsOpenCREModal(false);
    navigate('/portfolio/loan-level-data/cre');
  };

  useEffect(() => {
    if (location.pathname.includes('/cre/') && !isOpenCREModal) {
      setIsOpenCREModal(true);
      setCreModalLoanId(location.pathname.split('cre/')[1]);
    }
  }, [location.pathname]);

  useEffect(() => {
    dispatch({
      type: DataContextActionType.SET_LOADING,
      payload: loading,
    });
  }, [loading]);

  useEffect(() => {
    startTourRef.current = startTour;
  }, [startTour]);

  // Update portfolio context when pool selection changes
  useEffect(() => {
    if (selectedPool) {
      setSelectedPoolsData(transformSelectedPoolsData([selectedPool.poolData]));
    } else {
      setSelectedPoolsData([]);
    }
  }, [selectedPool, setSelectedPoolsData]);

  usePoolSelection({
    setSelectedPool,
  });

  const { setColumnOrder } = useColumnOrder(
    `loanTable_${getLoanTableConfigName(gqlAssetClass)}`,
    state.globalTableOrderConfig?.myPortfolio?.loanTable[
      getLoanTableConfigName(gqlAssetClass)
    ] as string[],
  );

  const {
    selectedLoans,
    setSelectedLoans,
    selectionStatus,
    setSelectionStatus,
    onClear,
    onSelectPage,
    onSelectAll,
  } = useSelection(params, data);

  // Auto-start tour on page load (wait for data to be ready)
  useEffect(() => {
    if (hasStartedTours.current) {
      return;
    }

    if (tourDataLoading || !gqlAssetClass || !filterProps.filtersConfig || loading) {
      return;
    }

    const checkTourElementsReady = (): boolean => {
      const firstElement = document.querySelector(
        '[data-tour="stratifications-portfolio-summary"]',
      );
      return firstElement !== null;
    };

    const attemptStartTour = () => {
      if (hasStartedTours.current || tourDataLoading || loading) {
        return;
      }

      if (checkTourElementsReady()) {
        retryAttemptsRef.current = 0;
        timeoutRef.current = setTimeout(() => {
          if (hasStartedTours.current || tourDataLoading || loading) {
            return;
          }
          hasStartedTours.current = true;

          // Get base steps from config
          const baseSteps = TOUR_CONFIGS['portfolio.loan-level-data']?.steps || [];

          startTourRef.current('portfolio.loan-level-data', { steps: baseSteps });
          timeoutRef.current = null;
        }, TOUR_START_DELAY_MS);
      } else if (retryAttemptsRef.current < MAX_RETRY_ATTEMPTS) {
        retryAttemptsRef.current += 1;
        timeoutRef.current = setTimeout(attemptStartTour, RETRY_DELAY_MS);
      } else {
        console.warn('[Tour] Loan Level Data tour elements not found after max retries');
      }
    };

    attemptStartTour();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [tourDataLoading, gqlAssetClass, filterProps.filtersConfig, loading]);

  // Handle SelectionStatusLine tour trigger when it becomes visible
  const hasStartedSelectionStatusLineTour = useRef(false);
  const selectionStatusLineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if SelectionStatusLine is visible (has content)
    const checkSelectionStatusLineVisible = (): boolean => {
      const element = selectionStatusLineRef.current;
      if (!element) return false;
      const statusLineDiv = element.querySelector('.bg-blue-600');
      return statusLineDiv !== null;
    };

    const isVisible = checkSelectionStatusLineVisible();

    // Reset flag when SelectionStatusLine becomes invisible
    if (!isVisible) {
      hasStartedSelectionStatusLineTour.current = false;
      return;
    }

    if (
      hasStartedSelectionStatusLineTour.current ||
      tourDataLoading ||
      !gqlAssetClass ||
      !filterProps.filtersConfig ||
      loading
    ) {
      return;
    }

    // Only start tour if SelectionStatusLine is visible
    const timer = setTimeout(() => {
      if (hasStartedSelectionStatusLineTour.current || !checkSelectionStatusLineVisible()) {
        return;
      }
      hasStartedSelectionStatusLineTour.current = true;
      startTour('portfolio.loan-level-data.selection-status-line');
    }, 500);

    return () => clearTimeout(timer);
  }, [
    tourDataLoading,
    gqlAssetClass,
    filterProps.filtersConfig,
    loading,
    selectionStatus,
    selectedLoans,
    startTour,
  ]);

  if (!gqlAssetClass || !filterProps.filtersConfig) {
    return <PortfolioLayout />;
  }

  const dataToShow = data || previousData;

  // Extract data based on whether we're showing pool or company data
  const loans = selectedPool
    ? (dataToShow as any)?.user?.company?.pool?.loans
    : (dataToShow as any)?.user?.company?.loans;

  const performanceData = selectedPool
    ? (dataToShow as any)?.user?.company?.pool?.performance_summary
    : (dataToShow as any)?.user?.company?.performanceData;

  // @ts-ignore
  const loanCount = performanceData?.loanCount || 0;
  // In v1 mode, totalLoans represents the filtered loan count
  const totalLoans = loanCount;

  const potentialPoolSize =
    selectionStatus === SelectionStatus.AllSelected
      ? totalLoans
      : Object.keys(selectedLoans).length;

  // Auto-start create pool button tour when loans are selected
  const hasStartedCreatePoolTour = useRef(false);

  useEffect(() => {
    // Reset when no loans are selected
    if (potentialPoolSize === 0) {
      hasStartedCreatePoolTour.current = false;
      return;
    }

    if (
      hasStartedCreatePoolTour.current ||
      tourDataLoading ||
      !gqlAssetClass ||
      !filterProps.filtersConfig ||
      loading ||
      potentialPoolSize === 0
    ) {
      return;
    }

    // Start tour when loans are selected (button is always in DOM)
    const timer = setTimeout(() => {
      if (hasStartedCreatePoolTour.current) {
        return;
      }
      hasStartedCreatePoolTour.current = true;
      startTour('portfolio.loan-level-data.create-pool-button');
    }, 300);

    return () => clearTimeout(timer);
  }, [
    tourDataLoading,
    gqlAssetClass,
    filterProps.filtersConfig,
    loading,
    potentialPoolSize,
    startTour,
  ]);

  const onSubmitCreatePoolForm = (formValues: FormValues) => {
    createPool(formValues, gqlAssetClass, queryFilters, selectedLoans, selectionStatus)
      .then((result) => {
        if (result.type === 'sync') {
          // Pool created synchronously - show success dialog
          poolCreatedDialog.openDialog();
        } else {
          // Pool creation started as async job - show job started dialog
          poolCreationStartedDialog.openDialog();
        }
      })
      .catch((error) => {
        toast.error(`Error creating pool: "${error}".`);
        setErrorMessage(
          'There was an error creating the loan pool. Please retry or contact support.',
        );
        errorDialog.openDialog();
      });
  };

  const handlePoolChange = (option: { label: string; value: string; poolData?: Pool } | null) => {
    setSelectedPool(
      option as {
        label: string;
        value: string;
        poolData: Pool;
      },
    );
    setPool(option?.value || null);
  };

  const fetchAllLoans = async () => {
    const PAGE_SIZE = 100;
    let accumulated: any[] = [];
    let offset = 0;
    let hasMore = true;

    const sortVariables = {
      sort_field: loansSorting[0]?.id,
      sort_direction: loansSorting[0]?.desc ? SortDirection.desc : SortDirection.asc,
    };

    while (hasMore) {
      try {
        if (selectedPool) {
          // Fetch pool loans
          const { data } = await apolloClient.query<GetPoolLoans, GetPoolLoansVariables>({
            query: GET_POOL_LOANS_QUERY,
            variables: {
              poolId: selectedPool.value,
              sort: sortVariables,
              pagination: {
                offset: offset,
                page_size: PAGE_SIZE,
              },
              filters: queryFilters,
            },
            fetchPolicy: 'no-cache',
          });

          const loans = data?.user?.company?.pool?.loans ?? [];
          accumulated = [...accumulated, ...loans];

          if (loans.length < PAGE_SIZE) {
            hasMore = false;
          }
        } else {
          // Fetch company loans
          const transformedQueryFilters = createQueryFilters(gqlAssetClass, queryFilters);

          const { data } = await apolloClient.query<GetDrilldown, GetDrilldownVariables>({
            query: GET_DRILLDOWN_QUERY,
            variables: {
              sort: sortVariables,
              pagination: {
                offset: offset,
                page_size: PAGE_SIZE,
              },
              filters: transformedQueryFilters,
            },
            fetchPolicy: 'no-cache',
          });

          const loans = data?.user?.company?.loans ?? [];
          accumulated = [...accumulated, ...loans];

          if (loans.length < PAGE_SIZE) {
            hasMore = false;
          }
        }

        offset += PAGE_SIZE;
      } catch (error) {
        toast.error('Error fetching loan data for download.');
        throw error;
      }
    }

    return accumulated;
  };

  const handleDownloadExcel = async () => {
    if (!loans) {
      toast.error('No data available for download.');
      return;
    }

    setIsDownloading(true);

    try {
      const selectedLoansIds = Object.keys(selectedLoans)?.map((id) => id.split(':')[1]);

      let downloadData: typeof loans;

      // If specific loans are selected (not "All Selected"), download only those
      if (selectedLoansIds.length > 0 && selectionStatus !== SelectionStatus.AllSelected) {
        downloadData = loans.filter((loan: (typeof loans)[number]) =>
          selectedLoansIds.includes(loan.id),
        );
      } else {
        // Fetch all loans matching the current filter
        toast.loading('Fetching all loans for download...', { id: 'download' });
        downloadData = await fetchAllLoans();

        toast.success(
          `Downloaded ${downloadData.length} loan${downloadData.length !== 1 ? 's' : ''}`,
          { id: 'download' },
        );
      }

      downloadExcelFromJson(downloadData, 'Loan Data', 'Loan Data.xlsx');
    } catch {
      toast.error('Failed to download loan data.', { id: 'download' });
    } finally {
      setIsDownloading(false);
    }
  };

  //if need to select loans which are not in a pool
  // const filterSelectableLoans = (
  //   newSelection: Updater<RowSelectionState>,
  //   loans: GetDrilldown_user_company_loans[],
  // ): Updater<RowSelectionState> => {
  //   return Object.fromEntries(
  //     Object.entries(newSelection).filter(([key]) => {
  //       const loanId = key.split(':')[1];
  //       const loan = loans.find((loan) => loan.id === loanId);
  //       return !loan?.listing;
  //     }),
  //   );
  // };

  return (
    <>
      <InProgressOverlay
        title="Creating Pool In Progress"
        description={`Creating pool of ${potentialPoolSize.toLocaleString()} loans`}
        open={createPoolLoading}
        hideButton={true}
      />

      {isOpenCREModal ? (
        <CreDetailsModal
          handleCloseCREModal={handleCloseCREModal}
          creModalLoanId={creModalLoanId}
          loansData={loans}
          role="SELLER"
        />
      ) : (
        <PortfolioLayout
          filterRow={
            <div className="flex w-full items-center justify-between gap-4">
              <div className="flex-1" data-tour="loan-level-data-pill-filters">
                <PillFilters {...filterProps} filtersConfig={filterProps.filtersConfig} />
              </div>
              <div className="flex items-center gap-2" data-tour="loan-level-data-pool-selector">
                <span className="text-sm text-gray-600">Pool:</span>
                <Dropdown
                  options={poolOptions || []}
                  value={selectedPool?.label}
                  className="w-48 border-none"
                  placeholder="Select Pool"
                  onValueChange={(value: any) => {
                    handlePoolChange(value);
                  }}
                  onClear={() => handlePoolChange(null)}
                  showClear={true}
                />
              </div>
            </div>
          }
          actions={
            <div className="ml-4 flex items-center gap-2">
              <ModalNotStyled
                trigger={
                  <div data-tour="loan-level-data-create-pool-button">
                    <Button
                      type="primary_dark"
                      disabled={potentialPoolSize === 0}
                      label="select loans"
                      className="w-max"
                    >
                      {potentialPoolSize === 0
                        ? 'Select loans to create a pool'
                        : `Create pool of ${potentialPoolSize.toLocaleString()} loans`}
                    </Button>
                  </div>
                }
              >
                {({ closeModal }) => (
                  <CreatePoolForm closeModal={closeModal} createPool={onSubmitCreatePoolForm} />
                )}
              </ModalNotStyled>
              <div data-tour="loan-level-data-download-button">
                <DownloadDocumentButton
                  onClick={handleDownloadExcel}
                  fileType="excel"
                  tooltipText={
                    isDownloading
                      ? 'Downloading...'
                      : selectionStatus === SelectionStatus.AllSelected ||
                          Object.keys(selectedLoans).length === 0
                        ? 'Download all loans (filtered)'
                        : `Download ${Object.keys(selectedLoans).length} selected loan${Object.keys(selectedLoans).length !== 1 ? 's' : ''}`
                  }
                  tooltipPosition="left"
                  className="mr-4"
                  disabled={isDownloading}
                />
              </div>
            </div>
          }
        >
          {loans ? (
            <>
              <div ref={selectionStatusLineRef} data-tour="loan-level-data-selection-status-line">
                <SelectionStatusLine
                  status={selectionStatus}
                  numberSelectedOnPage={Object.keys(selectedLoans).length}
                  totalItems={totalLoans}
                  onClear={onClear}
                  onSelectAll={onSelectAll}
                />
              </div>
              <LoanTable
                //@ts-ignore
                data={loans ?? []}
                assetClass={gqlAssetClass}
                strings={{
                  empty: selectedPool ? 'No loans in this pool' : 'No Loans Found',
                }}
                selection={{
                  selected: selectedLoans,
                  onSelectionChange: (newSelection) => {
                    //if need to select loans which are not in a pool
                    // const filteredSelection = filterSelectableLoans(
                    //   newSelection,
                    //   loans,
                    // );

                    setSelectedLoans(newSelection);
                    setSelectionStatus(SelectionStatus.None);
                  },
                  onHeaderCheckboxChange: (checked) => {
                    if (checked) {
                      onSelectPage();
                    } else {
                      onClear();
                    }
                  },
                }}
                sorting={{
                  state: loansSorting,
                  onSortingChanged: setLoansSorting,
                }}
                pagination={{
                  updateParams: updateParams,
                  queryParams: params,
                  totalItems: loanCount,
                }}
                setColumnOrder={setColumnOrder}
                handleCREClick={
                  isCreEnabled && gqlAssetClass === AssetClass.CRE ? handleCREClick : undefined
                }
              />
            </>
          ) : (
            <div className="flex h-64 items-center justify-center text-gray-500">
              {!selectedPool ? 'Select a pool to view loans' : 'No data available'}
            </div>
          )}

          {/* For debugging, show list of selected account ids */}
          {/* <div>Selected Loans:</div>
      <div>{JSON.stringify(selectedLoans)}</div> */}

          <PoolSuccessDialog
            dialog={poolCreatedDialog}
            title="Pool Created"
            message={`"${lastCreatedPool?.friendlyName}" is now available. Would you like to go to the Pool Manager?`}
            imageAlt="pool created"
            actions={
              <div className="flex w-[150px] justify-between gap-[30px]">
                <Button
                  size="medium"
                  type="secondary"
                  label="no"
                  onClick={() => poolCreatedDialog.closeDialog()}
                >
                  No
                </Button>
                <Button
                  size="medium"
                  label="yes"
                  onClick={() => {
                    poolCreatedDialog.closeDialog();
                    navigateToPoolManager();
                  }}
                >
                  Yes
                </Button>
              </div>
            }
          />

          <PoolSuccessDialog
            dialog={poolCreationStartedDialog}
            title="Pool Creation Started"
            message={`"${pendingPoolJob?.friendlyName}" is being created. This may take a few minutes for large pools. You can track progress in the Jobs dropdown in the header.`}
            imageAlt="pool creation started"
            actions={
              <div className="flex justify-center">
                <Button
                  size="medium"
                  label="ok"
                  onClick={() => poolCreationStartedDialog.closeDialog()}
                >
                  OK
                </Button>
              </div>
            }
          />

          <Dialog dialog={errorDialog}>
            <>
              <div>{errorMessage}</div>
              <DialogActions>
                <Button size="medium" label="cancel" onClick={() => errorDialog.closeDialog()}>
                  OK
                </Button>
              </DialogActions>
            </>
          </Dialog>
        </PortfolioLayout>
      )}
    </>
  );
};

export default LoanLevelData;
